import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { MgmDashboard as UIFoundationsMgmDashboard } from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import { getImageMappedProps } from '../../../../utils/props-mappings'
import { RewardProps, useDashboardData } from '../common/use-dashboard-data'
import MgmDashboardEditConfig from './DashboardEditConfig'
import {
  CardModelProps,
  CardTypes,
  DashboardModelProps,
  GodFatherAction,
  GodLinkAction,
  MgmDataTypes
} from './type'
import {
  REPLACE_CODE,
  REPLACE_DATE,
  REPLACE_POINTS,
  REPLACE_REWARD,
  REPLACE_SCALE,
  REPLACE_VALUE
} from '../../../../utils/constants'
import { replacePlaceholders } from '../../../../utils/replace-placeholders'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'

type MgmDashboardProps = React.ComponentProps<typeof UIFoundationsMgmDashboard>

type MgmDashboardCardProps = NonNullable<
  MgmDashboardProps['referralItems']
>[number]

const getIsRewardDisabled = (reward: RewardProps) =>
  !!(reward?.burnedDate || new Date(reward?.expirationDate) < new Date())

type RewardDateLabelProps = {
  reward: RewardProps
  display: MgmDataTypes
  godLinkAction: GodLinkAction
  date?: Date
}

const getRewardDateLabel = ({
  reward,
  display,
  godLinkAction,
  date
}: RewardDateLabelProps) => {
  let dateLabel = null
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    if (display === MgmDataTypes.GfAction) {
      if (reward?.burnedDate) {
        dateLabel = godLinkAction?.redeemedLabel
      } else if (reward?.expirationDate) {
        if (new Date(reward?.expirationDate) < new Date()) {
          dateLabel = (godLinkAction as GodFatherAction)?.expiredOnLabel
        } else {
          dateLabel = (godLinkAction as GodFatherAction)?.expiresOnLabel
        }
      }
    } else if (display === MgmDataTypes.GsAction) {
      dateLabel = godLinkAction?.redeemedLabel
    }
  }
  if (dateLabel) {
    return replacePlaceholders(dateLabel, [
      [REPLACE_DATE, `<b>${date?.toLocaleDateString()}</b>`]
    ])
  }
  return ''
}

const Dashboard = (props: { model: DashboardModelProps }) => {
  const { id, general, gfAction, gsAction, ...rest } = props?.model || {}
  const editableProps = useExtractEditableProps(props, rest)
  const data = useDashboardData()

  // eslint-disable-next-line no-confusing-arrow
  const getCardPropsFromReferralItem = (itemType: string): CardModelProps =>
    itemType === CardTypes.Loyalty ? general?.loyaltyCard : general?.voucherCard

  const godLinkAction =
    general?.display === MgmDataTypes.GfAction ? gfAction : gsAction

  const configDashboard: MgmDashboardProps = {
    title: data?.mgmReferralCode || '-',
    titleLabel: general?.referralCode?.label || '',
    getProgressLabel: (value?: number, scale?: number) =>
      replacePlaceholders(general?.referralCode?.progressLabel, [
        [REPLACE_VALUE, value?.toString()],
        [REPLACE_SCALE, scale?.toString()]
      ]) || '',
    value: data?.referrals?.length || 0,
    scale: data?.maxGodsonsReferral || 0,
    referralItems: (data?.rewards?.map((reward: RewardProps) => {
      const usedDate = new Date(
        general?.display === MgmDataTypes.GfAction
          ? reward?.burnedDate || reward?.expirationDate
          : reward?.referralDate
      )
      const isDisabled = getIsRewardDisabled(reward)
      const cardProps = getCardPropsFromReferralItem(reward?.type)
      const getDateLabel = (date?: Date) =>
        getRewardDateLabel({
          reward,
          display: general?.display,
          godLinkAction,
          date
        })
      return {
        isDisabled,
        title:
          replacePlaceholders(cardProps?.message, [
            [REPLACE_REWARD, reward?.value],
            [REPLACE_POINTS, reward?.loyaltyPoints?.toString()],
            [REPLACE_CODE, reward?.couponCode]
          ]) || '',
        date: usedDate,
        copyMessage: general?.voucherCard?.copyMessage,
        ...(cardProps?.image
          ? {
              imageProps: {
                ...getImageMappedProps(cardProps?.image)
              }
            }
          : {}),
        ...(reward?.type !== CardTypes.Loyalty
          ? {
              getDateLabel: (date?: Date) => getDateLabel(date) || '',
              onClick: () => {
                navigator.clipboard
                  .writeText(reward?.couponCode || '')
                  .catch(() => {
                    // Silently handle clipboard errors
                  })
              }
            }
          : {})
      } as MgmDashboardCardProps
    }) || []) as MgmDashboardProps['referralItems'],
    id
  }

  return (
    <EditableComponent {...editableProps}>
      {MgmDashboardEditConfig.isEmpty(props) ? (
        MgmDashboardEditConfig.emptyLabel
      ) : (
        <UIFoundationsMgmDashboard {...configDashboard} />
      )}
    </EditableComponent>
  )
}

const DashboardWithErrorBoundary = withErrorBoundary(
  withComponentRefresh(Dashboard),
  {
    componentName: 'Dashboard',
    fallback: <ErrorFallback componentName="Dashboard" />
  }
)

export default DashboardWithErrorBoundary
